/**
 * Microsoft Entra ID (Azure AD) SSO helpers — Web (confidential-client) flow.
 *
 * No MSAL.js needed. The frontend simply:
 *   1. Builds Microsoft's /authorize URL and redirects the browser to it.
 *   2. After Microsoft signs the user in and bounces back to /auth/callback?code=…,
 *      it captures the code and POSTs it to /api/auth/microsoft.
 *   3. Backend exchanges the code for tokens (using the client secret),
 *      validates the id_token, and sets our HttpOnly session cookie.
 *
 * Azure config (client_id / tenant_id) is fetched from /api/auth/config so the
 * backend remains the single source of truth.
 */

let runtimeConfig = null;
let configPromise = null;

const REDIRECT_PATH = "/auth/callback";
const STATE_KEY = "msft_oauth_state";

/** Fetch (and cache) Azure SSO config from the backend. */
export async function getAuthConfig() {
  if (runtimeConfig) return runtimeConfig;
  if (configPromise) return configPromise;
  const API = process.env.REACT_APP_BACKEND_URL;
  configPromise = fetch(`${API}/api/auth/config`, { credentials: "include" })
    .then((r) => {
      if (!r.ok) throw new Error(`Failed to fetch auth config: ${r.status}`);
      return r.json();
    })
    .then((cfg) => {
      runtimeConfig = cfg;
      return cfg;
    });
  return configPromise;
}

export function getRedirectUri() {
  return `${window.location.origin}${REDIRECT_PATH}`;
}

/** Cryptographically-random state string for CSRF protection. */
function randomState() {
  const a = new Uint8Array(16);
  window.crypto.getRandomValues(a);
  return Array.from(a, (b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * Build Microsoft's authorize URL and navigate the browser to it.
 * Stores a state string in sessionStorage so /auth/callback can verify it.
 */
export async function startMicrosoftLogin() {
  const cfg = await getAuthConfig();
  if (!cfg.azure_enabled || !cfg.client_id || !cfg.tenant_id) {
    throw new Error("Microsoft SSO is not configured on the server.");
  }
  const state = randomState();
  sessionStorage.setItem(STATE_KEY, state);

  const params = new URLSearchParams({
    client_id:     cfg.client_id,
    response_type: "code",
    redirect_uri:  getRedirectUri(),
    response_mode: "query",
    scope:         "openid profile email offline_access",
    state,
    prompt:        "select_account",
  });
  window.location.assign(
    `https://login.microsoftonline.com/${cfg.tenant_id}/oauth2/v2.0/authorize?${params.toString()}`,
  );
}

/**
 * Validate the state echoed back by Microsoft to ensure the response is for
 * this browser session. Returns true if valid.
 */
export function consumeOauthState(returnedState) {
  const expected = sessionStorage.getItem(STATE_KEY);
  sessionStorage.removeItem(STATE_KEY);
  return Boolean(expected) && expected === returnedState;
}
